"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Link, usePathname } from "@/navigation";
import { useTranslations } from "next-intl";
import { Menu, X } from "lucide-react";
import navbarIcon from "@/assets/images/navbar_icon.svg";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// The public nav, shared by the marketing pages (landing, pricing, faq) so they
// all offer the same way in. ModernNavBar stands down on these routes rather
// than stacking the in-app chrome on top of this bar.
//
// Copy lives under the "Landing" namespace, which is where these labels started
// and where they are still translated.
const MarketingNavBar: React.FC = () => {
  const t = useTranslations("Landing");
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // The bar persists across route changes, so without this the panel would stay
  // open on top of the page the visitor just navigated to.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // All of these are registered in the `pathnames` config, so the locale-aware
  // Link types them and emits /{locale}/... directly rather than relying on the
  // middleware to redirect an unprefixed href.
  const links = [
    { href: "/browse", label: t("Nav.BrowseVenues") },
    { href: "/pre-registration", label: t("Nav.ForOwners") },
    { href: "/pricing", label: t("Nav.Pricing") },
    { href: "/faq", label: t("Nav.Faq") },
  ] as const;

  return (
    <nav
      style={{ position: "sticky", top: 0, zIndex: 50 }}
      className="bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-700/50"
    >
      {/* The horizontal layout only appears at lg. Between md and lg there is not
          enough room for four Romanian labels plus the language switcher, the
          login link and the CTA: they wrapped onto two lines and the first link
          collided with the wordmark. */}
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4 h-16">
        <Link href="/" className="flex items-center gap-2 no-underline shrink-0">
          <Image src={navbarIcon} alt="Atlivo" width={26} height={26} className="h-6 w-auto" priority />
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
            >
              {label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 lg:gap-3 shrink-0">
          <LanguageSwitcher compact />
          <Link
            href="/sign-up"
            className="text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap hidden lg:block"
          >
            {t("Nav.LogIn")}
          </Link>
          <Link
            href="/sign-up"
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors whitespace-nowrap"
          >
            {t("Nav.GetStarted")}
          </Link>

          <button
            type="button"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            aria-label={open ? t("Nav.CloseMenu") : t("Nav.OpenMenu")}
            aria-expanded={open}
            aria-controls="marketing-mobile-menu"
            className="lg:hidden -mr-2 p-2 text-gray-400 hover:text-white transition-colors"
          >
            {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Toggled with a CSS class rather than conditionally rendered: the links
          stay in the HTML whatever the menu state, which is what keeps them
          visible to crawlers and is why the desktop row uses `hidden` too. */}
      <div
        id="marketing-mobile-menu"
        className={`${open ? "block" : "hidden"} lg:hidden border-t border-gray-700/50`}
      >
        <div className="px-6 py-3 flex flex-col">
          {links.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="py-3 text-sm text-gray-300 hover:text-white transition-colors border-b border-gray-700/40 last:border-b-0"
            >
              {label}
            </Link>
          ))}
          <Link
            href="/sign-up"
            className="py-3 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {t("Nav.LogIn")}
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default MarketingNavBar;
